/**
 * UnitViewMode — functions which determine whether to render the single-page (new) or
 * multi-page (legacy) unit plan layout.
 *
 * Extracted from IBPlanner/modules/Utils.js to break the circular dependency
 * edge:  modules/NavigationModule/NavigationThunks.js
 *      → IBPlanner/modules/Utils.js
 *
 */
import _ from "lodash";
import ACLStore from "lib/aclStore";
import { store } from "store/createStore";
import {
  OLD_UNIT_VIEW,
  NEW_UNIT_VIEW,
} from "UnitPlanDetails/modules/stringConstants";
import {
  getUserInfo,
  isToddleLibraryRoute,
} from "Login/modules/LoginModule/LoginUtils";
import { getSettingValue } from "modules/PermissionModule";
import { checkIsUnitPlanViewRevampEnabledInLibrary } from "Utils/ACLStoreUtils";
import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";

/**
 * NOTE: getIsLearningCoursesEnabledForUnit lives here temporarily. Its ideal
 * place lies in UnitPlans/modules/Utils, but UnitPlans/modules/Utils --> UnitViewModeUtils --> further cycles
 * prevents the move back.
 */
export const getIsLearningCoursesEnabledForUnit = () =>
  Boolean(
    _.get(
      store?.getState(),
      "planner.unitPlanData.learningCourseId",
      getIsLearningCoursesEnabled()
    )
  );

export const isSinglePageEnabled = () =>
  ACLStore.can("FeatureFlag:EnableSinglePageUnitPlan") ||
  ACLStore.can("FeatureFlag:RemoveUnitUISwitcher") ||
  getIsLearningCoursesEnabled();

export const getUnitViewType = () => {
  // If RemoveUnitUISwitcher is enabled, always return NEW_UNIT_VIEW
  if (ACLStore.can("FeatureFlag:RemoveUnitUISwitcher")) {
    return NEW_UNIT_VIEW;
  }

  const isSinglePage = isSinglePageEnabled();
  const isLearningCourseUnit = getIsLearningCoursesEnabledForUnit();

  if (isLearningCourseUnit) {
    return NEW_UNIT_VIEW;
  }
  if (!isSinglePage) {
    return OLD_UNIT_VIEW;
  }

  const isLibraryUnitPlanViewRevampEnabled = checkIsUnitPlanViewRevampEnabledInToddleLibrary();

  if (isLibraryUnitPlanViewRevampEnabled) {
    return NEW_UNIT_VIEW;
  }

  const userInfo = getUserInfo();
  const userId = _.get(userInfo, "id", "");
  const userType = _.get(userInfo, "user_type", "");
  const existingUnitViewType = getSettingValue({
    name: "UnitViewType",
    userId,
    readFromUserAndOrgLevel: true,
  });
  if (!_.isEqual(userType, "staff")) {
    return NEW_UNIT_VIEW;
  }
  return _.isEmpty(existingUnitViewType) ? OLD_UNIT_VIEW : existingUnitViewType;
};

const SINGLE_PAGE_ENABLED_ROUTES = [
  "courseOverview",
  ["curriculum-insights", "unit"],
  ["curriculum-insights", "poi"],
];

export const isSinglePageEnabledForRoute = () => {
  const currentUrl = window.location.href;

  if (!currentUrl) return false;

  // not using hasIncludeGivenPathInCurrentRoute since it causes a circular dependency
  return _.some(SINGLE_PAGE_ENABLED_ROUTES, route => {
    if (_.isArray(route)) {
      // some routes are generic, so we also check for their parent module routes along with them
      return _.every(route, subRoute => currentUrl.includes(`/${subRoute}`));
    } else {
      return currentUrl.includes(`/${route}`);
    }
  });
};

export const isSinglePageAndNewUnitView = () => {
  // for certain routes, we always want to show the single page layout
  if (isSinglePageEnabledForRoute()) {
    return true;
  }

  const isSinglePage = isSinglePageEnabled();
  const unitViewType = getUnitViewType();
  const isLibraryUnitPlanViewRevampEnabled = checkIsUnitPlanViewRevampEnabledInToddleLibrary();

  // If it's not a single page layout, return false immediately.
  if (!isSinglePage) return false;

  // For other screens(edit mode of fields in courseOverView), we will only check if isSinglePageEnabled is true,
  // as new field components will be displayed each time for those screens.(with exception of Toddle Library - remove the check once we give support for toddle library)
  if (isToddleLibraryRoute()) {
    return isLibraryUnitPlanViewRevampEnabled;
  } else {
    return unitViewType === NEW_UNIT_VIEW;
  }
};

export const checkIsUnitPlanViewRevampEnabledInToddleLibrary = () => {
  return checkIsUnitPlanViewRevampEnabledInLibrary() && isToddleLibraryRoute();
};
